import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, Edit2, Check, X, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../utils/api';

const EMPTY = { label: '', url: '/', sort_order: 0, is_active: true, opens_blank: false };

// Helper — gjithmonë dërgo '1'/'0' për boolean fields
const toPayload = (item) => ({
  label:       item.label,
  url:         item.url,
  sort_order:  item.sort_order || 0,
  is_active:   item.is_active ? '1' : '0',
  opens_blank: item.opens_blank ? '1' : '0',
});

export default function AdminNav() {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editId,   setEditId]   = useState(null);
  const [editForm, setEditForm] = useState({});
  const [addForm,  setAddForm]  = useState(EMPTY);
  const [adding,   setAdding]   = useState(false);
  const [saving,   setSaving]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/nav')
      .then(r => setItems(r.data))
      .catch(() => toast.error('Gabim gjatë ngarkimit. A ekziston tabela nav_items?'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Inline edit
  const startEdit = (item) => {
    setEditId(item.id);
    setEditForm({
      label:       item.label,
      url:         item.url,
      sort_order:  item.sort_order || 0,
      is_active:   item.is_active === 1 || item.is_active === true,
      opens_blank: item.opens_blank === 1 || item.opens_blank === true,
    });
  };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      await api.put(`/admin/nav/${id}`, toPayload(editForm));
      toast.success('U ruajt.');
      setEditId(null);
      load();
    } catch { toast.error('Gabim gjatë ruajtjes.'); }
    finally { setSaving(false); }
  };

  // ── Toggle active
  const toggleActive = async (item) => {
    const newVal = item.is_active === 1 || item.is_active === true ? false : true;
    try {
      await api.put(`/admin/nav/${item.id}`, toPayload({ ...item, is_active: newVal }));
      load();
    } catch { toast.error('Gabim.'); }
  };

  // ── Move order (swap sort_order me fqinjin)
  const moveItem = async (item, dir) => {
    const sorted = [...items].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex(x => x.id === item.id);
    const neighbour = sorted[idx + dir];
    if (!neighbour) return;
    try {
      await Promise.all([
        api.put(`/admin/nav/${item.id}`,      toPayload({ ...item,      sort_order: neighbour.sort_order })),
        api.put(`/admin/nav/${neighbour.id}`, toPayload({ ...neighbour, sort_order: item.sort_order })),
      ]);
      load();
    } catch { toast.error('Gabim.'); }
  };

  // ── Delete
  const handleDelete = async (id, label) => {
    if (!window.confirm(`Fshi "${label}" nga menyja?`)) return;
    try {
      await api.delete(`/admin/nav/${id}`);
      toast.success('U fshi.');
      load();
    } catch { toast.error('Gabim.'); }
  };

  // ── Add new
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addForm.label.trim()) return toast.error('Emri është i detyrueshëm.');
    if (!addForm.url.trim())   return toast.error('URL është e detyrueshme.');
    setAdding(true);
    try {
      await api.post('/admin/nav', toPayload(addForm));
      toast.success(`"${addForm.label}" u shtua në meny!`);
      setAddForm(EMPTY);
      load();
    } catch { toast.error('Gabim.'); }
    finally { setAdding(false); }
  };

  const isActive = (item) => item.is_active === 1 || item.is_active === true;
  const sorted = [...items].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">🔗 Menyja e Navigimit</h1>
          <p className="admin-page-sub">
            Menaxhoni linqet e menysë kryesore — shtoni, fshini, ndryshoni renditjen
          </p>
        </div>
      </div>

      <div className="card" style={{marginBottom:'1rem',padding:'1rem 1.5rem',background:'#EFF6FF',border:'1px solid #BFDBFE'}}>
        <p style={{fontSize:'0.82rem',color:'#1E40AF',lineHeight:1.6}}>
          💡 <strong>Si funksionon:</strong> Klikoni <strong>✏️</strong> për të ndryshuar emrin/URL.
          Klikoni <strong>▲▼</strong> për të ndryshuar renditjen.
          Klikoni butonin <strong>Aktiv/Joaktiv</strong> për ta fshehur/treguar pa e fshirë.
          Ndryshimet reflektohen <strong>menjëherë</strong> në menynë e faqes.
        </p>
      </div>

      {loading ? (
        <div className="spinner"/>
      ) : (
        <div className="card" style={{marginBottom:'1.5rem'}}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{width:80,textAlign:'center'}}>Renditja</th>
                  <th>Emri në meny</th>
                  <th>URL / Linku</th>
                  <th style={{textAlign:'center',width:120}}>Statusi</th>
                  <th style={{textAlign:'center',width:120}}>Veprimet</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((item, idx) => (
                  <tr key={item.id} style={{
                    background: isActive(item) ? '#fff' : '#F9FAFB',
                    opacity: isActive(item) ? 1 : 0.6,
                  }}>

                    {editId === item.id ? (
                      <>
                        <td style={{textAlign:'center'}}>
                          <input type="number" className="form-input"
                            style={{width:60,padding:'4px 6px',textAlign:'center'}}
                            value={editForm.sort_order}
                            onChange={e => setEditForm(p => ({...p, sort_order: Number(e.target.value)}))}/>
                        </td>
                        <td>
                          <input className="form-input" style={{padding:'6px 10px'}}
                            placeholder="Emri..."
                            value={editForm.label}
                            onChange={e => setEditForm(p => ({...p, label: e.target.value}))}/>
                        </td>
                        <td>
                          <input className="form-input" style={{padding:'6px 10px'}}
                            placeholder="/departamentet ose https://..."
                            value={editForm.url}
                            onChange={e => setEditForm(p => ({...p, url: e.target.value}))}/>
                        </td>
                        <td style={{textAlign:'center'}}>
                          <label style={{cursor:'pointer',fontSize:'0.82rem',display:'flex',alignItems:'center',gap:6,justifyContent:'center'}}>
                            <input type="checkbox"
                              checked={editForm.is_active}
                              onChange={e => setEditForm(p => ({...p, is_active: e.target.checked}))}/>
                            Aktiv
                          </label>
                        </td>
                        <td>
                          <div style={{display:'flex',gap:4,justifyContent:'center'}}>
                            <button className="btn btn-primary btn-sm"
                              onClick={() => saveEdit(item.id)} disabled={saving}>
                              <Check size={13}/> {saving ? '...' : 'Ruaj'}
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={() => setEditId(null)}>
                              <X size={13}/>
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>
                          <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{padding:'2px 7px',minWidth:0}}
                              onClick={() => moveItem(item, -1)}
                              disabled={idx === 0}
                            ><ArrowUp size={12}/></button>
                            <span style={{fontSize:'0.8rem',fontWeight:700,color:'var(--gray-600)',minWidth:24,textAlign:'center'}}>
                              {item.sort_order}
                            </span>
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{padding:'2px 7px',minWidth:0}}
                              onClick={() => moveItem(item, 1)}
                              disabled={idx === sorted.length - 1}
                            ><ArrowDown size={12}/></button>
                          </div>
                        </td>
                        <td>
                          <span style={{
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            color: isActive(item) ? 'var(--gray-900)' : 'var(--gray-400)',
                          }}>
                            {item.label}
                          </span>
                          {(item.opens_blank === 1 || item.opens_blank === true) && (
                            <span style={{fontSize:'0.68rem',color:'var(--gray-400)',marginLeft:6}}>
                              ↗ hapet jashtë
                            </span>
                          )}
                        </td>
                        <td>
                          <code style={{fontSize:'0.78rem',color:'var(--gray-500)',background:'var(--gray-50)',padding:'2px 6px',borderRadius:4}}>
                            {item.url}
                          </code>
                        </td>
                        <td style={{textAlign:'center'}}>
                          <button
                            onClick={() => toggleActive(item)}
                            style={{
                              padding:'5px 14px',
                              fontSize:'0.75rem',
                              fontWeight:600,
                              borderRadius:20,
                              border:'none',
                              cursor:'pointer',
                              background: isActive(item) ? '#D1FAE5' : '#F3F4F6',
                              color:      isActive(item) ? '#065F46' : '#6B7280',
                              transition:'all 0.15s',
                            }}
                          >
                            {isActive(item) ? '✓ Aktiv' : '✗ Fshehur'}
                          </button>
                        </td>
                        <td>
                          <div style={{display:'flex',gap:4,justifyContent:'center'}}>
                            <button className="btn btn-secondary btn-sm"
                              onClick={() => startEdit(item)} title="Ndrysho">
                              <Edit2 size={13}/>
                            </button>
                            <button className="btn btn-danger btn-sm"
                              onClick={() => handleDelete(item.id, item.label)} title="Fshi">
                              <Trash2 size={13}/>
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{textAlign:'center',padding:'3rem',color:'var(--gray-400)'}}>
                      Nuk ka menu. Shtoni menunë e parë poshtë.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── SHTO MENU TË RE ──────────────────────────── */}
      <div className="card" style={{padding:'1.5rem'}}>
        <h2 style={{fontSize:'0.95rem',fontWeight:700,marginBottom:'1.25rem',color:'var(--gray-900)'}}>
          ➕ Shto link të ri në meny
        </h2>
        <form onSubmit={handleAdd}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1.2fr 80px 1fr auto',gap:'10px',alignItems:'end',flexWrap:'wrap'}}>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Emri që shfaqet *</label>
              <input className="form-input" placeholder="p.sh. Historiku"
                value={addForm.label}
                onChange={e => setAddForm(p => ({...p, label: e.target.value}))}/>
            </div>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">URL *</label>
              <input className="form-input"
                placeholder="/faqe/historiku  ose  /kontakti  ose  https://..."
                value={addForm.url}
                onChange={e => setAddForm(p => ({...p, url: e.target.value}))}/>
            </div>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Nr. rend.</label>
              <input className="form-input" type="number" min="0"
                value={addForm.sort_order}
                onChange={e => setAddForm(p => ({...p, sort_order: Number(e.target.value)}))}/>
            </div>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label" style={{display:'flex',alignItems:'center',gap:6}}>
                <input type="checkbox"
                  checked={addForm.opens_blank}
                  onChange={e => setAddForm(p => ({...p, opens_blank: e.target.checked}))}/>
                <ExternalLink size={12}/> Hap në tab të re
              </label>
            </div>
            <button type="submit" className="btn btn-primary" disabled={adding}
              style={{whiteSpace:'nowrap',height:38}}>
              {adding ? 'Duke shtuar...' : <><Plus size={14}/> Shto</>}
            </button>
          </div>

          <div style={{marginTop:10,padding:'10px 14px',background:'var(--gray-50)',borderRadius:6,fontSize:'0.75rem',color:'var(--gray-500)'}}>
            <strong>Shembuj URL:</strong>
            &nbsp;faqet tuaja dinamike → <code>/faqe/historiku</code>
            &nbsp;|&nbsp; seksione → <code>/departamentet</code>, <code>/lajmet</code>
            &nbsp;|&nbsp; linqe të jashtme → <code>https://msh.rks-gov.net</code>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}
